import { useEffect, useMemo, useState } from 'preact/hooks'
import Image from 'snippets/image/image'
import Price from 'snippets/price/price'
import { addCartItem } from 'libs/cart'
import useInCart from 'hooks/useInCart'
import Icon from 'snippets/icon/icon'
import LoaderSpin from 'snippets/loader-spin/loader-spin'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { hideModal } from 'helpers/dom'
import { getChildBundleItemsBySKU } from 'hooks/useStorefront'
import { getSkuQueryEdges } from 'helpers/storefront-graphql-helper'

function ProductCard({
  className = 'relative flex items-center mb-4 rounded bg-grey-100 last:mb-0',
  title,
  handle,
  imageUrl,
  alt = title,
  variants,
  size,
  idModal,
  ratioImage = 'aspect-[1/1]',
  classImageWrapper = 'relative w-[124px] min-w-[124px]',
  isCover = false,
  soldOutLabel = '',
  classContentWrapper = 'flex flex-1 items-center justify-between gap-4 pl-4',
  bundleChildSkus = null,
  showDiscountBadge = false,
  includesLabel = '',
  saveLabel = ''
}) {
  const [currentVariant, setCurrentVariant] = useState(variants[0])
  const [reRenderKey, setRenderKey] = useState(variants[0].id)
  const [reRenderATC, setReRenderATC] = useState(variants[0].id)
  const [isAdding, setIsAdding] = useState(false)
  const [bundleProducts, setBundleProducts] = useState([])

  const { isAvailable, maxVariantQuantity } = useInCart(currentVariant)

  const productUrl = `/products/${handle}?variant=${currentVariant.id}`

  const currentImageUrl = useMemo(() => currentVariant?.image || currentVariant?.featured_image?.src || imageUrl, [currentVariant])

  // Function to fetch bundle product data
  const fetchBundleProducts = async (bundleChildSkus) => {
    if (!bundleChildSkus) return []

    try {
      const bundleItems = bundleChildSkus
        .split(', ')
        .map(item => {
          const [sku, qty] = item.split('#')
          if (!sku) return null
          return { sku: sku.trim(), qty: parseInt(qty, 10) || 1 }
        })
        .filter(Boolean)

      const skus = bundleItems.map(item => item.sku)
      const response = await getChildBundleItemsBySKU(skus)

      if (response.ok) {
        const data = await response.json()
        const products = getSkuQueryEdges(data).map(x => x.node).filter(Boolean)

        // Match products with quantities
        const qtyMap = Object.fromEntries(bundleItems.map(({ sku, qty }) => [sku, qty]))

        const productsWithQty = products.map(prod => {
          const variantSku = prod.variants.edges[0]?.node.sku
          return {
            ...prod,
            qty: qtyMap[variantSku] || 1
          }
        })

        return productsWithQty
      }
    } catch (error) {
      console.error('Error fetching bundle products:', error)
    }
  }

  useEffect(() => {
    setRenderKey(reRenderKey + 1)
  }, [currentImageUrl])

  useEffect(() => {
    setReRenderATC(reRenderATC + 1)
  }, [maxVariantQuantity])

  useEffect(() => {
    let cancelled = false

    if (bundleChildSkus) {
      fetchBundleProducts(bundleChildSkus).then(products => {
        if (!cancelled) {
          setBundleProducts(products)
        }
      })
    }

    return () => { cancelled = true }
  }, [bundleChildSkus])

  const addToCart = async () => {
    setIsAdding(true)
    const properties = {}
    const cartSingle = await addCartItem(currentVariant.id, properties, 1)
    if (cartSingle) {
      globalEmitter.emit(emitterProps.cart.add, cartSingle)
      hideModal(idModal)
    }
    setIsAdding(false)
  }

  const classSizePrice = () => {
    let classSize = 'text-base'
    if (currentVariant.compare_at_price && currentVariant.compare_at_price !== 0) {
      const newClassSize = currentVariant.compare_at_price > currentVariant.price ? ' text-grey-300' : ' text-grey-700'
      classSize += newClassSize
    }
    return classSize
  }

  const calculateDiscountPercentage = () => {
    if (currentVariant.compare_at_price && currentVariant.compare_at_price > currentVariant.price) {
      const discount = ((currentVariant.compare_at_price - currentVariant.price) / currentVariant.compare_at_price) * 100
      return Math.round(discount)
    }
    return null
  }

  return (
    <div className={className}>
      <div className={classImageWrapper}>
        <a className={'cy-product-card'} href={productUrl} title={title}>
          <Image
            imageUrl={[currentImageUrl]}
            alt={alt}
            className={ratioImage}
            imageClass="w-full h-full"
            imageBreakpoints="(min-width: 768px)|(min-width: 0px)"
            isCover={isCover}
            isLazy={true}
            key={`${handle}-${reRenderKey}`}
          />
        </a>
      </div>
      <div className={classContentWrapper}>
        <div className="flex flex-col flex-1">
          {!isAvailable && (
            <span className="w-fit rounded py-0.5 px-1.5 mb-1.5 bg-grey-200 text-xs text-gray-500 font-medium">{soldOutLabel}</span>
          )}
          <h3 className="font-sans text-sm font-medium text-grey-1000">
            <a href={productUrl} title={title} className="break-words text-sm !tracking-normal text-grey-1000">
              {title}
            </a>
          </h3>
          <div className="mb-1 text-sm text-grey-500 product-card__size">{size}</div>
          <div className="mb-1">
            {bundleProducts.length > 0 && (
              <div>
                <div className="mb-1 text-sm text-grey-500">{includesLabel}:</div>
                <ul className="pr-1 space-y-1 text-xs text-grey-500">
                  {bundleProducts.map((product, index) => (
                    <li key={index} className="flex text-sm">
                      <span>{product.qty} x {product.title}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {variants && variants.length && (
            <Price
              className="flex justify-start gap-[6px] text-grey-700"
              classSize={classSizePrice()}
              price={currentVariant.price}
              originalPrice={currentVariant.compare_at_price}
              classColor="!text-accent-4"
            />
          )}
        </div>
        {variants && variants.length && (
          <div className="flex justify-center item-center">
            <button
              type="button"
              title='add to cart'
              className="relative flex items-center justify-center w-[34px] h-[34px] rounded-none rounded-r text-sm font-bold tracking-normal"
              disabled={!isAvailable ? 'true' : ''}
              key={reRenderATC}
              onClick={addToCart}
            >
              {isAdding ? <LoaderSpin /> : (
                <div className="relative">
                  {isAvailable ? (
                    <Icon name="icon-quick-add" className='w-[34px] h-[34px]' />
                  ) : (
                    <Icon name="icon-quick-add-disabled" className='w-[34px] h-[34px]' />
                  )}
                </div>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductCard
